import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Menu, Moon, Sun, X } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "../../lib/utils";

const DM_SANS_LINK_ID = "finance-demo-dm-sans";

const FINANCE_PAGES = {
  dashboard: "dashboard",
  transactions: "transactions",
  accounts: "accounts",
  categories: "categories",
  budget: "budget",
  allocation: "allocation",
  goals: "goals",
  recurring: "recurring",
  assets: "assets",
  reports: "reports",
  import: "import",
};

const NAV_SECTIONS = [
  { title: "Overview", items: [{ id: FINANCE_PAGES.dashboard, label: "Dashboard" }] },
  {
    title: "Track & manage",
    items: [
      { id: FINANCE_PAGES.transactions, label: "Transactions" },
      { id: FINANCE_PAGES.accounts, label: "Accounts & balances" },
      { id: FINANCE_PAGES.categories, label: "Categories" },
    ],
  },
  {
    title: "Plan",
    items: [
      { id: FINANCE_PAGES.budget, label: "Budget" },
      { id: FINANCE_PAGES.allocation, label: "Allocation calculator" },
      { id: FINANCE_PAGES.goals, label: "Savings goals" },
      { id: FINANCE_PAGES.recurring, label: "Recurring bills" },
    ],
  },
  {
    title: "Assets & reports",
    items: [
      { id: FINANCE_PAGES.assets, label: "Assets" },
      { id: FINANCE_PAGES.reports, label: "Reports & export" },
      { id: FINANCE_PAGES.import, label: "Spreadsheet import" },
    ],
  },
];

const PAGE_TITLES = {
  [FINANCE_PAGES.dashboard]: "Dashboard",
  [FINANCE_PAGES.transactions]: "Transactions",
  [FINANCE_PAGES.accounts]: "Accounts & balances",
  [FINANCE_PAGES.categories]: "Categories",
  [FINANCE_PAGES.budget]: "Budget",
  [FINANCE_PAGES.allocation]: "Allocation calculator",
  [FINANCE_PAGES.goals]: "Savings goals",
  [FINANCE_PAGES.recurring]: "Recurring bills",
  [FINANCE_PAGES.assets]: "Assets",
  [FINANCE_PAGES.reports]: "Reports & export",
  [FINANCE_PAGES.import]: "Spreadsheet import",
};

function formatIdr(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(n);
}

const MOCK_ACCOUNTS = [
  { name: "Main account", balance: 12_450_000 },
  { name: "Emergency savings", balance: 28_000_000 },
  { name: "Digital wallet", balance: 1_200_000 },
];

const MOCK_BUDGET = [
  { category: "Home & living", spent: 4_200_000, planned: 5_000_000, over: false },
  { category: "Transport", spent: 980_000, planned: 1_200_000, over: false },
  { category: "Entertainment", spent: 1_450_000, planned: 1_000_000, over: true },
];

const MOCK_TX = [
  ["2026-04-05", "Expense", "Dining", "Digital wallet", "—", -185_000],
  ["2026-04-04", "Income", "Salary", "Main account", "—", 9_500_000],
  ["2026-04-03", "Transfer", "—", "Main account", "Emergency savings", -1_000_000],
  ["2026-04-02", "Expense", "Transport", "Digital wallet", "—", -45_000],
];

const CASHFLOW = [
  { label: "Nov", net: 2_100_000 },
  { label: "Dec", net: -400_000 },
  { label: "Jan", net: 3_200_000 },
  { label: "Feb", net: 1_800_000 },
  { label: "Mar", net: 900_000 },
  { label: "Apr", net: 2_400_000 },
];

const INSIGHT_SEGMENTS = [
  { key: "needs", label: "Needs", amount: 4_200_000, pct: 52 },
  { key: "wants", label: "Wants", amount: 2_100_000, pct: 26 },
  { key: "savings", label: "Savings", amount: 900_000, pct: 11 },
  { key: "other", label: "Other", amount: 880_000, pct: 11 },
];

function segmentColor(key) {
  if (key === "needs") return "bg-emerald-500";
  if (key === "wants") return "bg-amber-500";
  if (key === "savings") return "bg-sky-500";
  return "bg-rose-400";
}

function cardClass() {
  return "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900";
}

function inputClass() {
  return "min-h-[44px] rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100";
}

function SidebarNav({ activePage, onSelectPage }) {
  return (
    <nav className="flex flex-col gap-5 text-sm" aria-label="Main menu (demo)">
      {NAV_SECTIONS.map((section) => (
        <div key={section.title}>
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            {section.title}
          </p>
          <div className="flex flex-col gap-1">
            {section.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelectPage(item.id)}
                className={cn(
                  "group flex items-center gap-2 rounded-xl px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900",
                  activePage === item.id
                    ? "border border-sky-100/80 bg-sky-50 font-semibold text-sky-700 shadow-sm dark:border-sky-900/40 dark:bg-sky-950/50 dark:text-sky-400"
                    : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function PageIntro({ title, children }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-3xl">{title}</h1>
      {children ? (
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500 dark:text-slate-400">{children}</p>
      ) : null}
    </div>
  );
}

function DashboardView({ navigateTo }) {
  return (
    <>
      <PageIntro title="Dashboard">
        Summary for <strong>Apr 2026</strong> (IDR). Use the menu to switch pages — sample data only, no backend.
      </PageIntro>
      <div
        className="mb-6 flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:flex-row sm:flex-wrap sm:items-end"
        aria-hidden
      >
        <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
          <span className="font-medium text-slate-600 dark:text-slate-300">Month</span>
          <select disabled className={inputClass()} defaultValue="4">
            <option value="4">Apr</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
          <span className="font-medium text-slate-600 dark:text-slate-300">Year</span>
          <input type="number" disabled className={`${inputClass()} sm:w-28`} defaultValue={2026} />
        </label>
        <button type="button" disabled className="min-h-[44px] rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white opacity-60 shadow-sm">
          Apply
        </button>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigateTo(FINANCE_PAGES.transactions)}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-sky-700"
        >
          + New transaction
        </button>
        <button
          type="button"
          onClick={() => navigateTo(FINANCE_PAGES.import)}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
        >
          Import data
        </button>
        <button
          type="button"
          onClick={() => navigateTo(FINANCE_PAGES.reports)}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
        >
          Reports
        </button>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Income", value: 9_500_000, cls: "text-emerald-600 dark:text-emerald-400" },
          { label: "Expenses", value: 6_230_000, cls: "text-rose-600 dark:text-rose-400" },
          { label: "Savings (allocated)", value: 1_000_000, cls: "text-sky-600 dark:text-sky-400" },
          { label: "Net (in − out)", value: 3_270_000, cls: "text-slate-900 dark:text-slate-50" },
        ].map((c) => (
          <div
            key={c.label}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ring-slate-900/5 transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:ring-white/5"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{c.label}</p>
            <p className={cn("mt-2 text-xl font-semibold", c.cls)}>{formatIdr(c.value)}</p>
          </div>
        ))}
      </div>

      <div className="mb-8 rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50/90 to-white p-5 shadow-sm dark:border-sky-900/50 dark:from-sky-950/30 dark:to-slate-900 sm:p-6">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">Smart summary</h2>
            <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
              Sample insights (fictional data). A live app would compute these from real transactions.
            </p>
          </div>
          <span className="shrink-0 text-[10px] uppercase tracking-wider text-slate-400">vs Mar 2026</span>
        </div>
        <div className="mb-5 space-y-2 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
          <p>This month’s spending is ~8% lower than last month.</p>
          <p>Needs allocation is still under 60% of total spending.</p>
        </div>
        <div className="mb-5">
          <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Spending mix (needs / wants / other)
          </p>
          <div className="flex h-4 w-full overflow-hidden rounded-full ring-1 ring-slate-200/80 dark:ring-slate-700">
            {INSIGHT_SEGMENTS.map((seg) => (
              <div
                key={seg.key}
                title={`${seg.label}: ${formatIdr(seg.amount)}`}
                className={cn("h-full min-w-px shrink-0", segmentColor(seg.key))}
                style={{ width: `${seg.pct}%` }}
              />
            ))}
          </div>
          <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-600 dark:text-slate-300">
            {INSIGHT_SEGMENTS.map((seg) => (
              <li key={seg.key} className="flex items-center gap-1.5">
                <span className={cn("inline-block h-2.5 w-2.5 rounded-sm", segmentColor(seg.key))} />
                {seg.label} · {formatIdr(seg.amount)} ({seg.pct}%)
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="mb-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Tips & alerts</p>
          <ul className="space-y-3">
            <li className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/25 dark:text-amber-100">
              <p className="font-medium">Entertainment budget</p>
              <p className="mt-1 text-xs leading-relaxed opacity-90">Entertainment category is over the sample limit.</p>
            </li>
          </ul>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className={cardClass()}>
          <h2 className="mb-4 text-sm font-semibold">Balance by account</h2>
          <ul className="space-y-2">
            {MOCK_ACCOUNTS.map((a) => (
              <li key={a.name} className="flex justify-between border-b border-slate-100 pb-2 text-sm dark:border-slate-800">
                <span>{a.name}</span>
                <span className="font-medium">{formatIdr(a.balance)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={cardClass()}>
          <h2 className="mb-4 text-sm font-semibold">Assets overview</h2>
          <ul className="space-y-2 text-sm">
            <li className="flex justify-between">
              <span>Liquid</span>
              <span className="font-medium">{formatIdr(41_650_000)}</span>
            </li>
            <li className="flex justify-between">
              <span>Illiquid</span>
              <span className="font-medium">{formatIdr(120_000_000)}</span>
            </li>
          </ul>
          <button
            type="button"
            onClick={() => navigateTo(FINANCE_PAGES.assets)}
            className="mt-4 text-sm font-medium text-sky-600 hover:underline dark:text-sky-400"
          >
            Manage assets →
          </button>
        </div>
      </div>

      <div className={cn(cardClass(), "mb-8")}>
        <h2 className="mb-4 text-sm font-semibold">Progress budget (2026-04)</h2>
        <div className="space-y-3">
          {MOCK_BUDGET.map((b) => {
            const pct = Math.min(100, Math.round((b.spent / b.planned) * 100));
            return (
              <div key={b.category}>
                <div className="mb-1 flex justify-between text-xs">
                  <span>{b.category}</span>
                  <span className={cn(b.over ? "text-rose-600" : "text-slate-600 dark:text-slate-400")}>
                    {formatIdr(b.spent)} / {formatIdr(b.planned)}
                    {b.over ? " (over)" : ` (left ${formatIdr(b.planned - b.spent)})`}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <div
                    className={cn("h-full rounded-full", b.over ? "bg-rose-500" : "bg-sky-500")}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="mt-0.5 text-[10px] text-slate-400">Used: {pct}%</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className={cn(cardClass(), "mb-8")}>
        <h2 className="mb-4 text-sm font-semibold">Monthly cashflow</h2>
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={CASHFLOW} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "currentColor" }} className="text-slate-500" />
              <YAxis
                tick={{ fontSize: 11, fill: "currentColor" }}
                className="text-slate-500"
                tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`}
              />
              <Tooltip
                formatter={(v) => [formatIdr(v), "Net"]}
                contentStyle={{ borderRadius: "12px", border: "1px solid rgb(226 232 240)" }}
              />
              <Bar dataKey="net" fill="rgb(14 165 233)" radius={[4, 4, 0, 0]} opacity={0.75} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={cardClass()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Recent transactions</h2>
          <button
            type="button"
            onClick={() => navigateTo(FINANCE_PAGES.transactions)}
            className="text-sm font-medium text-sky-600 hover:underline dark:text-sky-400"
          >
            View all
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs text-slate-500 dark:border-slate-800">
                <th className="pb-2 pr-4">Date</th>
                <th className="pb-2 pr-4">Type</th>
                <th className="pb-2 pr-4">Category</th>
                <th className="pb-2 pr-4">Account</th>
                <th className="pb-2 pr-4">To account</th>
                <th className="pb-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_TX.map((row, i) => (
                <tr key={i} className="border-b border-slate-100 dark:border-slate-800/80">
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={cn("py-2 pr-4", j === 5 && "text-right font-medium", j === 0 && "whitespace-nowrap")}
                    >
                      {typeof cell === "number" ? formatIdr(cell) : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function TransactionsView() {
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("4");
  const [year, setYear] = useState("2026");
  const [txType, setTxType] = useState("");
  const [applied, setApplied] = useState(false);

  function handleFilterSubmit(e) {
    e.preventDefault();
    setApplied(true);
  }

  return (
    <>
      <PageIntro title="Transactions">
        Search and filter (local demo — nothing is sent to the server). Amounts in IDR.
      </PageIntro>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <span className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white opacity-80 shadow-sm">
          + Add transaction
        </span>
      </div>
      <form onSubmit={handleFilterSubmit} className={cn(cardClass(), "mb-6")} aria-label="Transaction filters">
        <fieldset className="m-0 min-w-0 border-0 p-0">
          <legend className="mb-3 px-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">
            Filters & search
          </legend>
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Search text</span>
              <input
                className={inputClass()}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Payee / detail / notes"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Month</span>
              <select className={inputClass()} value={month} onChange={(e) => setMonth(e.target.value)}>
                {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map(
                  (mLabel, i) => (
                    <option key={mLabel} value={String(i + 1)}>
                      {mLabel}
                    </option>
                  )
                )}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Year</span>
              <input
                type="number"
                className={inputClass()}
                value={year}
                onChange={(e) => setYear(e.target.value)}
                min={2000}
                max={2100}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Type</span>
              <select className={inputClass()} value={txType} onChange={(e) => setTxType(e.target.value)}>
                <option value="">All</option>
                <option value="Income">Income</option>
                <option value="Expense">Expense</option>
                <option value="Transfer">Transfer</option>
              </select>
            </label>
            <div className="flex items-end lg:col-span-1">
              <button
                type="submit"
                className="min-h-[44px] w-full rounded-xl bg-sky-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-sky-700 sm:w-auto"
              >
                Apply filters
              </button>
            </div>
          </div>
        </fieldset>
        {applied ? (
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400" role="status">
            Filters stored in the browser (demo) — table still shows sample rows.
          </p>
        ) : null}
      </form>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-800/50">
              <th className="p-3">Date</th>
              <th className="p-3">Type</th>
              <th className="p-3">Category</th>
              <th className="p-3">Account</th>
              <th className="p-3">To account</th>
              <th className="p-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_TX.map((row, i) => (
              <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                {row.map((cell, j) => (
                  <td key={j} className={cn("p-3", j === 5 && "text-right font-medium")}>
                    {typeof cell === "number" ? formatIdr(cell) : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AccountsView() {
  const [name, setName] = useState("");
  function handleAdd(e) {
    e.preventDefault();
    setName("");
  }
  return (
    <>
      <PageIntro title="Accounts & balances">List of accounts and approximate balances (sample).</PageIntro>
      <div className={cn(cardClass(), "mb-6")}>
        <h2 className="mb-3 text-sm font-semibold">Add account (demo)</h2>
        <form onSubmit={handleAdd} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <input
            className={`${inputClass()} flex-1`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Account name"
          />
          <button
            type="submit"
            className="min-h-[44px] rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
          >
            Save
          </button>
        </form>
      </div>
      <div className={cardClass()}>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {MOCK_ACCOUNTS.map((a) => (
            <li key={a.name} className="flex items-center justify-between py-3 text-sm first:pt-0 last:pb-0">
              <span className="font-medium">{a.name}</span>
              <span>{formatIdr(a.balance)}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

function CategoriesView() {
  const rows = [
    { name: "Salary", kind: "Income", group: "Needs" },
    { name: "Dining", kind: "Expense", group: "Needs" },
    { name: "Entertainment", kind: "Expense", group: "Wants" },
    { name: "Emergency fund", kind: "Savings", group: "Savings" },
  ];
  return (
    <>
      <PageIntro title="Categories">
        Income, expenses, and savings — grouped as needs / wants / savings (sample).
      </PageIntro>
      <div className={cn(cardClass(), "mb-6")}>
        <h2 className="mb-3 text-sm font-semibold">Add category (demo)</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <input className={inputClass()} placeholder="Name" />
          <select className={inputClass()} defaultValue="Expense">
            <option>Income</option>
            <option>Expense</option>
            <option>Savings</option>
          </select>
          <select className={inputClass()} defaultValue="Needs">
            <option>Needs</option>
            <option>Wants</option>
            <option>Savings</option>
            <option>Custom</option>
          </select>
          <button
            type="button"
            className="min-h-[44px] rounded-xl bg-sky-600 text-sm font-semibold text-white hover:bg-sky-700"
          >
            Save
          </button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-800/50">
              <th className="p-3">Name</th>
              <th className="p-3">Kind</th>
              <th className="p-3">Group</th>
              <th className="p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.name} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3">{c.kind}</td>
                <td className="p-3">{c.group}</td>
                <td className="p-3">
                  <button type="button" className="text-xs text-sky-600 hover:underline dark:text-sky-400">
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function BudgetView() {
  return (
    <>
      <PageIntro title="Budget">Allocation per category for the current month (sample).</PageIntro>
      <div className={cardClass()}>
        <div className="space-y-4">
          {MOCK_BUDGET.map((b) => {
            const pct = Math.min(100, Math.round((b.spent / b.planned) * 100));
            return (
              <div
                key={b.category}
                className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
              >
                <div className="mb-2 flex justify-between text-sm font-medium">
                  <span>{b.category}</span>
                  <span className={b.over ? "text-rose-600" : "text-slate-600 dark:text-slate-400"}>
                    {formatIdr(b.planned)} / month
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <div
                    className={cn("h-full rounded-full", b.over ? "bg-rose-500" : "bg-sky-500")}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Spent {formatIdr(b.spent)} — {pct}%
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function AllocationView() {
  const [income, setIncome] = useState(10_000_000);
  const needs = Math.round(income * 0.5);
  const wants = Math.round(income * 0.3);
  const save = income - needs - wants;
  return (
    <>
      <PageIntro title="Allocation calculator">
        Split income in a 50/30/20 style — calculated only in the browser.
      </PageIntro>
      <div className={cardClass()}>
        <label className="mb-6 block text-sm">
          <span className="mb-2 block font-medium text-slate-700 dark:text-slate-300">Monthly income (IDR)</span>
          <input
            type="number"
            className={inputClass()}
            value={income}
            min={0}
            onChange={(e) => setIncome(Number(e.target.value) || 0)}
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 dark:border-emerald-900/40 dark:bg-emerald-950/20">
            <p className="text-xs font-medium text-emerald-800 dark:text-emerald-200">Needs ~50%</p>
            <p className="mt-1 text-lg font-semibold text-emerald-700 dark:text-emerald-300">{formatIdr(needs)}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
            <p className="text-xs font-medium text-amber-900 dark:text-amber-200">Wants ~30%</p>
            <p className="mt-1 text-lg font-semibold text-amber-800 dark:text-amber-300">{formatIdr(wants)}</p>
          </div>
          <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-4 dark:border-sky-900/40 dark:bg-sky-950/20">
            <p className="text-xs font-medium text-sky-900 dark:text-sky-200">Savings ~20%</p>
            <p className="mt-1 text-lg font-semibold text-sky-800 dark:text-sky-300">{formatIdr(save)}</p>
          </div>
        </div>
      </div>
    </>
  );
}

function GoalsView() {
  const goals = [
    { name: "6-month emergency fund", target: 50_000_000, current: 28_000_000 },
    { name: "Family vacation", target: 15_000_000, current: 6_200_000 },
  ];
  return (
    <>
      <PageIntro title="Savings goals">Progress toward targets (sample).</PageIntro>
      <div className="grid gap-4 sm:grid-cols-2">
        {goals.map((g) => {
          const pct = Math.min(100, Math.round((g.current / g.target) * 100));
          return (
            <div key={g.name} className={cardClass()}>
              <h3 className="font-semibold text-slate-900 dark:text-slate-50">{g.name}</h3>
              <p className="mt-1 text-sm text-slate-500">
                {formatIdr(g.current)} / {formatIdr(g.target)}
              </p>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div className="h-full rounded-full bg-sky-500" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-2 text-xs text-slate-400">{pct}% complete</p>
            </div>
          );
        })}
      </div>
    </>
  );
}

function RecurringView() {
  const items = [
    { name: "Home internet", amount: 350_000, day: 5 },
    { name: "Streaming", amount: 54_000, day: 12 },
    { name: "Electricity (estimate)", amount: 400_000, day: 20 },
  ];
  return (
    <>
      <PageIntro title="Recurring bills">Monthly debit schedule (sample).</PageIntro>
      <div className={cardClass()}>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((x) => (
            <li key={x.name} className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
              <div>
                <p className="font-medium">{x.name}</p>
                <p className="text-xs text-slate-500">On day {x.day} each month</p>
              </div>
              <span className="font-semibold text-slate-800 dark:text-slate-200">{formatIdr(x.amount)}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

function AssetsView() {
  const assets = [
    { name: "Mutual funds", type: "Liquid", value: 25_000_000 },
    { name: "Gold (grams)", type: "Illiquid", value: 45_000_000 },
    { name: "Vehicle", type: "Illiquid", value: 85_000_000 },
  ];
  return (
    <>
      <PageIntro title="Assets">Inventory of financial positions (sample).</PageIntro>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-800/50">
              <th className="p-3">Name</th>
              <th className="p-3">Type</th>
              <th className="p-3 text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => (
              <tr key={a.name} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3 font-medium">{a.name}</td>
                <td className="p-3">{a.type}</td>
                <td className="p-3 text-right">{formatIdr(a.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ReportsView() {
  return (
    <>
      <PageIntro title="Reports & export">Download summary (demo buttons — no file is generated).</PageIntro>
      <div className="flex flex-wrap gap-3">
        {["CSV transactions", "PDF summary", "Excel budget"].map((label) => (
          <button
            key={label}
            type="button"
            className="min-h-[44px] rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            {label}
          </button>
        ))}
      </div>
    </>
  );
}

function ImportView() {
  const [fileName, setFileName] = useState(null);
  return (
    <>
      <PageIntro title="Spreadsheet import">Choose a .csv / .xlsx file (demo — file is not uploaded).</PageIntro>
      <div
        className={cn(
          cardClass(),
          "flex min-h-[200px] flex-col items-center justify-center border-dashed text-center"
        )}
      >
        <label className="cursor-pointer rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700">
          Choose file
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            className="sr-only"
            onChange={(e) => setFileName(e.target.files?.[0]?.name ?? null)}
          />
        </label>
        {fileName ? (
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">Selected: {fileName}</p>
        ) : (
          <p className="mt-4 text-sm text-slate-500">Drag a file here (UI only)</p>
        )}
      </div>
    </>
  );
}

function FinanceMain({ activePage, navigateTo }) {
  switch (activePage) {
    case FINANCE_PAGES.dashboard:
      return <DashboardView navigateTo={navigateTo} />;
    case FINANCE_PAGES.transactions:
      return <TransactionsView />;
    case FINANCE_PAGES.accounts:
      return <AccountsView />;
    case FINANCE_PAGES.categories:
      return <CategoriesView />;
    case FINANCE_PAGES.budget:
      return <BudgetView />;
    case FINANCE_PAGES.allocation:
      return <AllocationView />;
    case FINANCE_PAGES.goals:
      return <GoalsView />;
    case FINANCE_PAGES.recurring:
      return <RecurringView />;
    case FINANCE_PAGES.assets:
      return <AssetsView />;
    case FINANCE_PAGES.reports:
      return <ReportsView />;
    case FINANCE_PAGES.import:
      return <ImportView />;
    default:
      return <DashboardView navigateTo={navigateTo} />;
  }
}

export default function FinanceDemo() {
  const [dark, setDark] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activePage, setActivePage] = useState(FINANCE_PAGES.dashboard);

  useEffect(() => {
    if (!document.getElementById(DM_SANS_LINK_ID)) {
      const link = document.createElement("link");
      link.id = DM_SANS_LINK_ID;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  function navigateTo(pageId) {
    setActivePage(pageId);
    setMobileOpen(false);
  }

  const shell = (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-50 text-slate-900 antialiased dark:bg-slate-950 dark:text-slate-100"
      style={{ fontFamily: '"DM Sans", system-ui, sans-serif' }}
    >
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="hidden w-60 shrink-0 flex-col gap-6 border-r border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/50 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none md:flex">
          <button
            type="button"
            onClick={() => navigateTo(FINANCE_PAGES.dashboard)}
            className="px-1 text-left rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
          >
            <p className="text-lg font-semibold leading-snug text-sky-700 dark:text-sky-500">My Financial planner</p>
          </button>
          <SidebarNav activePage={activePage} onSelectPage={navigateTo} />
          <div className="mt-auto border-t border-slate-200 pt-4 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setDark((d) => !d)}
              className="w-full rounded-xl px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {dark ? "Light mode" : "Dark mode"}
            </button>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className="sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/95 md:hidden">
            <div className="min-w-0">
              <p className="truncate text-xs text-slate-500 dark:text-slate-400">{PAGE_TITLES[activePage]}</p>
              <p className="truncate font-semibold text-sky-700 dark:text-sky-500">My Financial planner</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setDark((d) => !d)}
                className="flex min-h-[44px] items-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                aria-label={dark ? "Light mode" : "Dark mode"}
              >
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="flex min-h-[44px] items-center gap-1 rounded-xl bg-sky-600 px-3 py-2 text-sm font-medium text-white shadow-sm"
              >
                <Menu className="h-4 w-4" />
                Menu
              </button>
            </div>
          </header>

          {mobileOpen ? (
            <div className="fixed inset-0 z-40 flex md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
              <button
                type="button"
                className="absolute inset-0 bg-slate-900/50 dark:bg-black/60"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
              />
              <div className="relative z-10 ml-auto flex h-full w-[min(18rem,calc(100vw-2rem))] flex-col border-l border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900">
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-semibold text-sky-700 dark:text-sky-500">Menu</span>
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <SidebarNav activePage={activePage} onSelectPage={navigateTo} />
                </div>
              </div>
            </div>
          ) : null}

          <main
            id="main-content"
            className="mx-auto w-full max-w-7xl flex-1 overflow-y-auto p-4 pb-10 md:p-8 md:pb-12"
          >
            <FinanceMain activePage={activePage} navigateTo={navigateTo} />
          </main>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-slate-950">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-800 bg-slate-950 px-4 py-2.5">
        <Link
          to="/#projects"
          className="inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to portfolio
        </Link>
        <span className="rounded-full border border-slate-600 bg-slate-900 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
          Demo UI
        </span>
      </div>
      <div className={cn("flex min-h-0 flex-1", dark && "dark")}>{shell}</div>
    </div>
  );
}
